import React from 'react';
import { StyleSheet, View } from 'react-native';
import { useStyle } from 'react-native-style-utilities';
import {
  BodyText,
  Button,
  Chip,
  DisplayText,
  IconLink,
  TextField,
  theme,
  useBreakpointHelpers,
  useFluidSize,
} from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

import { DeliveryPreferenceType } from '../../graphql/types';

export const DeliveryPreferencesForm = ({
  preferences,
  submitting,
  onClose,
  onSave,
}: DeliveryPreferencesFormProps) => {
  const { currentBreakpoint } = useBreakpointHelpers();
  const { isMobile } = currentBreakpoint;
  const [localType, setLocalType] = React.useState<string>(preferences.type);
  const [localNotes, setLocalNotes] = React.useState(preferences.notes ?? '');

  React.useEffect(() => {
    setLocalType(preferences.type);
    setLocalNotes(preferences.notes ?? '');
  }, [preferences.type, preferences.notes]);
  const submitForm = React.useCallback(() => {
    onSave({
      type: localType as DeliveryPreferenceType,
      notes: localNotes,
    });
  }, [localNotes, localType, onSave]);

  return (
    <View style={isMobile ? styles.formMobile : styles.formDesktop}>
      <Header onClose={onClose} />
      <TypeSelector localType={localType} setLocalType={setLocalType} />
      <NotesField localNotes={localNotes} setLocalNotes={setLocalNotes} />
      <SubmitButton submitting={submitting} submitForm={submitForm} />
    </View>
  );
};

const Header = ({ onClose }: Partial<DeliveryPreferencesFormProps>) => {
  const { t } = useLocalizationContext();
  const { currentBreakpoint } = useBreakpointHelpers();
  const { isMobile } = currentBreakpoint;

  return (
    <View style={isMobile ? styles.column : styles.row}>
      {!isMobile && (
        <DisplayText size={3}>{t('delivery.preferences.title')}</DisplayText>
      )}
      <View style={styles.icon}>
        <IconLink
          testID="delivery.preferences.close-button"
          name={isMobile ? 'IconArrowLeft' : 'IconClose'}
          iconSize={24}
          accessibilityRole="button"
          accessibilityLabel={t('general.close')}
          accessibilityHint={t('general.close')}
          onPress={onClose}
        />
      </View>
      {isMobile ? (
        <DisplayText style={styles.header} size={3}>
          {t('delivery.preferences.title')}
        </DisplayText>
      ) : null}
    </View>
  );
};

const TypeSelector = ({
  localType,
  setLocalType,
}: {
  readonly localType: string;
  readonly setLocalType: (type: string) => void;
}) => {
  const { t } = useLocalizationContext();
  const fluid = useFluidSize({ min: 375, max: 425 });
  const paddingHorizontal = fluid(12, 16);
  const fluidPadding = useStyle(
    () => ({ paddingHorizontal }),
    [paddingHorizontal],
  );

  return (
    <View>
      <BodyText size={5} style={styles.sectionLabel}>
        {t('delivery.preferences.dropoff-label')}
      </BodyText>
      <Chip.Menu value={localType} onChange={setLocalType}>
        <Chip
          value={DeliveryPreferenceType.LeaveAtDoor}
          testID="delivery.preferences.form.leave-at-door"
          height={48}
          accessibilityHint={t('delivery.preferences.leave-at-door')}
          accessibilityLabel={t('delivery.preferences.leave-at-door')}
          style={fluidPadding}
        >
          {t('delivery.preferences.leave-at-door')}
        </Chip>
        <Chip
          value={DeliveryPreferenceType.Door}
          testID="delivery.preferences.form.meet-at-door"
          height={48}
          accessibilityHint={t('delivery.preferences.meet-at-door')}
          accessibilityLabel={t('delivery.preferences.meet-at-door')}
          style={fluidPadding}
        >
          {t('delivery.preferences.meet-at-door')}
        </Chip>
        <Chip
          value={DeliveryPreferenceType.Curbside}
          testID="delivery.preferences.form.meet-outside"
          height={48}
          accessibilityHint={t('delivery.preferences.meet-outside')}
          accessibilityLabel={t('delivery.preferences.meet-outside')}
          style={fluidPadding}
        >
          {t('delivery.preferences.meet-outside')}
        </Chip>
      </Chip.Menu>
    </View>
  );
};

const NotesField = ({
  localNotes,
  setLocalNotes,
}: {
  readonly localNotes: string;
  readonly setLocalNotes: (notes: string) => void;
}) => {
  const { t } = useLocalizationContext();

  return (
    <View style={styles.flex}>
      <View style={[styles.row, styles.sectionLabel]}>
        <BodyText size={5}>{t('delivery.preferences.note-label')}</BodyText>
        <BodyText
          size={5}
          style={localNotes.length === 128 && styles.cautionText}
        >
          {localNotes.length}/{128}
        </BodyText>
      </View>
      <TextField
        testID="delivery.preferences.notes"
        style={styles.inputField}
        value={localNotes}
        multiline
        maxLength={128}
        placeholder={t('delivery.preferences.note-placeholder')}
        accessibilityLabel={t('delivery.preferences.note-hint')}
        accessibilityHint={t('delivery.preferences.note-hint')}
        onChangeText={setLocalNotes}
      />
    </View>
  );
};

const SubmitButton = ({ submitting, submitForm }: SubmitButtonProps) => {
  const { t } = useLocalizationContext();
  const { currentBreakpoint } = useBreakpointHelpers();

  return (
    <Button
      size="large"
      width="100%"
      style={!currentBreakpoint.isMobile ? styles.button : undefined}
      onPress={submitForm}
      isLoading={submitting}
      testID="delivery.preferences.save-button"
      accessibilityLabel={t('delivery.preferences.save.a11y')}
      accessibilityHint={t('delivery.preferences.save.a11y')}
      accessibilityRole="button"
    >
      {t('delivery.preferences.save')}
    </Button>
  );
};

type SubmitButtonProps = {
  readonly submitting?: boolean;
  readonly submitForm: () => void;
};

type DeliveryPreferences = {
  readonly type: DeliveryPreferenceType;
  readonly notes?: string;
};

type DeliveryPreferencesFormProps = {
  readonly preferences: DeliveryPreferences;
  readonly submitting?: boolean;
  readonly onClose: () => void;
  readonly onSave: (preferences: DeliveryPreferences) => void;
};

const styles = StyleSheet.create({
  flex: {
    flex: 1,
  },
  formDesktop: {
    flex: 1,
    padding: theme.spacing.large,
  },
  formMobile: {
    flex: 1,
    paddingHorizontal: theme.spacing.medium,
    paddingVertical: theme.spacing.medium - theme.spacing.xsmall,
  },
  icon: {
    width: 24,
    height: 24,
  },
  header: {
    marginTop: theme.spacing.xlarge - theme.spacing.xsmall,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  column: {
    flexDirection: 'column',
  },
  sectionLabel: {
    marginTop: theme.spacing.xlarge,
    marginBottom: theme.spacing.small,
  },
  inputField: {
    height: 80,
  },
  cautionText: {
    color: theme.colors.CAUTION,
  },
  button: {
    marginHorizontal: theme.spacing.xlarge,
  },
});
